import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDrawerOpen } from '../../store/global';
import { TooltipHtml } from '../tooltip/tooltip.Html';
import { ColorsSwatch } from '../form/colorsSwatch';
import { PicSelectionOptions } from '../pic/pic.Selector';
import { motion } from 'framer-motion';
import { renderIconBySlug } from '../../content/icons';
import { FaTimes } from 'react-icons/fa';

const LABEL_STACK_THRESHOLD = 2; // percentage threshold for stacking labels
const LABEL_STACK_SPACING = 18; // px
const EDGE_MARGIN = 4;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.5;
const DEFAULT_COLOR = 'secondary';
const HORIZONTAL_AXIS_OFFSET = 15; // px distance from top for horizontal axes
const HORIZONTAL_TOP_PADDING = HORIZONTAL_AXIS_OFFSET + 8;
const VERTICAL_LABEL_PADDING = 140;
const COLOR_KEYS = [
  'secondary',
  'secondary-d',
  'secondary-l',
  'primary',
  'primary-l',
  'primary-d',
  'contrast',
  'contrast-l',
  'red',
  'orange',
  'yellow',
  'lime',
  'green',
  'blue',
  'purple',
  'pink',
];

const resolveColor = colorKey =>
  `rgb(var(--${colorKey || DEFAULT_COLOR}, var(--${DEFAULT_COLOR})))`;

const sanitizeString = value => (typeof value === 'string' ? value.trim() : '');

const formatTickKey = value => value.toFixed(6);

const useAxisConfig = ({ points, order, forcedRange }) => {
  const sortedPoints = useMemo(() => {
    const list = Array.isArray(points) ? [...points] : [];
    list.sort((a, b) => {
      if (order === 'desc') {
        return b.value - a.value;
      }
      return a.value - b.value;
    });
    return list;
  }, [points, order]);

  const values = useMemo(
    () => sortedPoints.map(point => point.value),
    [sortedPoints],
  );
  const computedMin = Math.min(...values);
  const computedMax = Math.max(...values);
  const min =
    typeof forcedRange?.min === 'number' ? forcedRange.min : computedMin;
  const max =
    typeof forcedRange?.max === 'number' ? forcedRange.max : computedMax;
  const range = max - min || 1;

  const fullMin = Math.min(min, computedMin);
  const fullMax = Math.max(max, computedMax);
  const fullRange = fullMax - fullMin || 1;
  const hasFixedBounds =
    typeof forcedRange?.min === 'number' &&
    typeof forcedRange?.max === 'number';
  const hasPartialBounds =
    typeof forcedRange?.min === 'number' ||
    typeof forcedRange?.max === 'number';
  const scaleFactor = hasPartialBounds ? 1 : Math.max(1, fullRange / range);

  const axisMin = hasFixedBounds ? min : fullMin;
  const axisMax = hasFixedBounds ? max : fullMax;
  const axisRange = Math.max(axisMax - axisMin, 1);

  return {
    sortedPoints,
    axisMin,
    axisMax,
    axisRange,
    scaleFactor,
  };
};

const useTickLabelMap = tickLabels =>
  useMemo(() => {
    if (!Array.isArray(tickLabels)) {
      return new Map();
    }
    const entries = tickLabels
      .map(entry => {
        const parsedValue = Number(entry?.value);
        if (!Number.isFinite(parsedValue)) {
          return null;
        }
        const labelTop = sanitizeString(entry?.labelTop);
        const labelBottom = sanitizeString(entry?.labelBottom);
        const legacyLabel = sanitizeString(entry?.label);
        const hasTop = labelTop !== '';
        const hasBottom = labelBottom !== '' || legacyLabel !== '';
        if (!hasTop && !hasBottom) {
          return null;
        }
        return {
          value: parsedValue,
          labelTop: hasTop ? labelTop : null,
          labelBottom: hasBottom ? labelBottom || legacyLabel : null,
        };
      })
      .filter(Boolean);

    const map = new Map();
    entries.forEach(entry => {
      map.set(formatTickKey(entry.value), {
        top: entry.labelTop,
        bottom: entry.labelBottom,
      });
    });
    return map;
  }, [tickLabels]);

const useAxisTicks = ({ axisMin, axisMax, axisRange, tickStep }) =>
  useMemo(() => {
    if (!Number.isFinite(axisMin) || !Number.isFinite(axisMax)) {
      return [];
    }
    const customStep =
      typeof tickStep === 'number' && Number.isFinite(tickStep) && tickStep > 0
        ? tickStep
        : null;
    const desiredTicks = 5;
    const rawStep = axisRange / Math.max(desiredTicks, 1);
    let step;
    if (customStep) {
      step = customStep;
    } else {
      if (!Number.isFinite(rawStep) || rawStep <= 0) {
        return [];
      }
      const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
      const normalized = rawStep / magnitude;
      step = magnitude;
      if (normalized >= 5) {
        step = 5 * magnitude;
      } else if (normalized >= 2) {
        step = 2 * magnitude;
      }
    }
    if (!Number.isFinite(step) || step <= 0) {
      return [];
    }
    const firstTick = Math.ceil(axisMin / step) * step;
    const ticks = [];
    for (let value = firstTick; value <= axisMax + step / 2; value += step) {
      if (value < axisMin - step / 2) {
        continue;
      }
      ticks.push(Number(value.toFixed(6)));
    }
    return ticks;
  }, [axisMin, axisMax, axisRange, tickStep]);

const usePointPositions = ({
  sortedPoints,
  axisMin,
  axisRange,
  order,
  isVertical,
}) =>
  useMemo(() => {
    return sortedPoints.map((point, index) => {
      const ratio =
        axisRange === 0
          ? index / Math.max(sortedPoints.length - 1, 1)
          : (point.value - axisMin) / axisRange;
      const directedRatio = order === 'desc' ? 1 - ratio : ratio;
      const position = EDGE_MARGIN + directedRatio * (100 - EDGE_MARGIN * 2);
      const isEdge = index === 0 || index === sortedPoints.length - 1;
      const labelAlign = isVertical
        ? 'right'
        : isEdge && (position < 10 || position > 90)
          ? position < 10
            ? 'left'
            : 'right'
          : 'center';
      return { point, position, labelAlign };
    });
  }, [sortedPoints, axisMin, axisRange, order, isVertical]);

const useStackedPoints = pointPositions =>
  useMemo(() => {
    const buckets = [];
    return pointPositions.map(data => {
      let bucket = buckets.find(
        entry =>
          Math.abs(entry.position - data.position) <= LABEL_STACK_THRESHOLD,
      );
      if (!bucket) {
        bucket = { position: data.position, items: [] };
        buckets.push(bucket);
      }
      const stackIndex = bucket.items.length;
      const pointData = { ...data, stackIndex, bucket };
      bucket.items.push(pointData);
      return pointData;
    });
  }, [pointPositions]);

const usePointDragHandlers = ({
  allowPointDrag,
  axisRef,
  axisMin,
  axisMax,
  axisRange,
  isVertical,
  order,
  tickStep,
  onChangeEntryValue,
  openDrawerOnClick,
  stopGridDrag,
  dispatch,
}) => {
  const [draggingEntryIndex, setDraggingEntryIndex] = useState(null);
  const dragPointerIdRef = useRef(null);
  const skipClickRef = useRef(false);
  const dragMovedRef = useRef(false);
  const lastDragValueRef = useRef(null);

  const convertPointerToValue = useCallback(
    (clientX, clientY) => {
      if (!axisRef.current) {
        return null;
      }
      const rect = axisRef.current.getBoundingClientRect();
      const size = isVertical ? rect.height : rect.width;
      if (!size || size <= 0) {
        return null;
      }
      const offset = isVertical ? clientY - rect.top : clientX - rect.left;
      const percent = (offset / size) * 100;
      if (!Number.isFinite(percent)) {
        return null;
      }
      const clampedPercent = Math.min(
        Math.max(percent, EDGE_MARGIN),
        100 - EDGE_MARGIN,
      );
      const ratio =
        (clampedPercent - EDGE_MARGIN) / (100 - EDGE_MARGIN * 2 || 1);
      const directedRatio = order === 'desc' ? 1 - ratio : ratio;
      return axisMin + directedRatio * axisRange;
    },
    [axisMin, axisRange, order, isVertical, axisRef],
  );

  const snapValueToStep = useCallback(
    value => {
      if (
        typeof tickStep !== 'number' ||
        !Number.isFinite(tickStep) ||
        tickStep <= 0
      ) {
        return value;
      }
      if (!Number.isFinite(value)) {
        return value;
      }
      const offset = value - axisMin;
      const steps = Math.round(offset / tickStep);
      const snapped = axisMin + steps * tickStep;
      return Math.min(Math.max(snapped, axisMin), axisMax);
    },
    [tickStep, axisMin, axisMax],
  );

  useEffect(() => {
    if (
      !allowPointDrag ||
      draggingEntryIndex === null ||
      typeof onChangeEntryValue !== 'function'
    ) {
      return;
    }
    const handlePointerMove = event => {
      if (
        dragPointerIdRef.current !== null &&
        typeof event.pointerId === 'number' &&
        event.pointerId !== dragPointerIdRef.current
      ) {
        return;
      }
      const nextValue = convertPointerToValue(event.clientX, event.clientY);
      if (nextValue === null) {
        return;
      }
      const snappedValue = snapValueToStep(nextValue);
      const lastValue = lastDragValueRef.current;
      if (
        lastValue &&
        lastValue.index === draggingEntryIndex &&
        Number(lastValue.value) === Number(snappedValue)
      ) {
        return;
      }
      dragMovedRef.current = true;
      lastDragValueRef.current = {
        index: draggingEntryIndex,
        value: snappedValue,
      };
      onChangeEntryValue(draggingEntryIndex, snappedValue);
    };
    const finishDrag = event => {
      if (
        dragPointerIdRef.current !== null &&
        typeof event.pointerId === 'number' &&
        event.pointerId !== dragPointerIdRef.current
      ) {
        return;
      }
      dragPointerIdRef.current = null;
      setDraggingEntryIndex(null);
      if (dragMovedRef.current) {
        skipClickRef.current = true;
        window.setTimeout(() => {
          skipClickRef.current = false;
        }, 0);
      }
      dragMovedRef.current = false;
      lastDragValueRef.current = null;
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
    };
  }, [
    allowPointDrag,
    draggingEntryIndex,
    onChangeEntryValue,
    convertPointerToValue,
    snapValueToStep,
  ]);

  const handlePointPointerDown = useCallback(
    (event, entryIndex) => {
      if (!allowPointDrag || typeof onChangeEntryValue !== 'function') {
        return;
      }
      stopGridDrag(event);
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      if (
        typeof event.button === 'number' &&
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }
      dragPointerIdRef.current =
        typeof event.pointerId === 'number' ? event.pointerId : null;
      dragMovedRef.current = false;
      skipClickRef.current = false;
      setDraggingEntryIndex(entryIndex);
    },
    [allowPointDrag, onChangeEntryValue, stopGridDrag],
  );

  const handlePointClick = useCallback(
    point => {
      if (!openDrawerOnClick) {
        return;
      }
      if (skipClickRef.current) {
        skipClickRef.current = false;
        return;
      }
      dispatch(setDrawerOpen(point.itemId));
    },
    [dispatch, openDrawerOnClick],
  );

  return { handlePointPointerDown, handlePointClick };
};

export const TimelineAll = props => {
  const containerRef = useRef(null);
  const axisRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useSelector(state => state.global.theme);
  const {
    points,
    order,
    emptyLabel,
    range: forcedRange,
    showLabels = true,
    showValues = true,
    showZoom = true,
    showTicks = true,
    mode = 'horizontal',
    onChangeEntryColor = null,
    onChangeEntryPic = null,
    onRemoveEntry = null,
    tickLabels,
    tickLabelsVertical = false,
    tickStep,
    onChangeEntryValue = null,
    showPointTooltips = true,
    openDrawerOnClick = true,
    allowPointDrag = true,
    disableLayoutAnimation = false,
  } = props;

  const isVertical = mode === 'vertical';
  const hasFixedBounds =
    typeof forcedRange?.min === 'number' &&
    typeof forcedRange?.max === 'number';
  const [zoomLevel, setZoomLevel] = useState(1);

  const { sortedPoints, scaleFactor, axisMin, axisMax, axisRange } =
    useAxisConfig({ points, order, forcedRange });

  useEffect(() => {
    setZoomLevel(1);
  }, [points]);

  const clampedZoom = Math.min(Math.max(zoomLevel, 1), MAX_ZOOM);
  const effectiveZoom = hasFixedBounds ? 1 : clampedZoom;

  useEffect(() => {
    if (!containerRef.current || scaleFactor !== 1) return;
    if (isVertical) {
      containerRef.current.scrollTop = 0;
    } else {
      containerRef.current.scrollLeft = 0;
    }
  }, [scaleFactor, isVertical]);

  const tickLabelMap = useTickLabelMap(tickLabels);
  const axisTicks = useAxisTicks({ axisMin, axisMax, axisRange, tickStep });
  const pointPositions = usePointPositions({
    sortedPoints,
    axisMin,
    axisRange,
    order,
    isVertical,
  });
  const stackedPoints = useStackedPoints(pointPositions);

  const stopGridDrag = useCallback(event => {
    event.stopPropagation();
  }, []);

  const { handlePointPointerDown, handlePointClick } = usePointDragHandlers({
    allowPointDrag,
    axisRef,
    axisMin,
    axisMax,
    axisRange,
    isVertical,
    order,
    tickStep,
    onChangeEntryValue,
    openDrawerOnClick,
    stopGridDrag,
    dispatch,
  });

  const sliderThemeVars = useMemo(() => {
    const toRgb = (value, fallback) =>
      typeof value === 'string' ? value.replace(/\s+/g, ', ') : fallback;
    const main = toRgb(theme?.main, '8, 11, 15');
    const primary = toRgb(theme?.primary, '3, 55, 59');
    const primaryDark = toRgb(theme?.['primary-d'], '21, 28, 36');
    return {
      '--timeline-track': `linear-gradient(90deg, rgba(${main}, 0.35), rgba(${primary}, 0.9), rgba(${primaryDark}, 0.9))`,
      '--timeline-thumb': `rgba(${primary}, 1)`,
      '--timeline-thumb-border': `rgba(${main}, 0.6)`,
      '--timeline-focus': `0 0 0 2px rgba(${primary}, 0.25)`,
    };
  }, [theme]);

  const handleZoomChange = useCallback(event => {
    setZoomLevel(Number(event.target.value));
  }, []);

  const renderPicPreview = (previewUrl, iconSlug, size = 30) => {
    if (iconSlug) {
      const iconNode = renderIconBySlug(iconSlug, {
        size,
        color: 'var(--contrast)',
      });
      return (
        <span className='inline-flex items-center justify-center w-full h-full text-contrast text-opacity-80'>
          {iconNode}
        </span>
      );
    }
    if (previewUrl) {
      return (
        <span
          className='inline-block w-full h-full rounded'
          style={{
            background: `url(${previewUrl}) center center / cover`,
          }}
        ></span>
      );
    }
    return (
      <span className='text-4xs uppercase text-contrast text-opacity-60 leading-none'>
        Ø
      </span>
    );
  };

  if (sortedPoints.length === 0) {
    return (
      <div className='text-xs text-contrast text-opacity-50 px-1 py-2'>
        {emptyLabel || 'Aucun élément dans la timeline pour le moment.'}
      </div>
    );
  }

  const pointsAreDraggable =
    allowPointDrag && typeof onChangeEntryValue === 'function';

  const baseScrollClass = isVertical
    ? 'timeline-scroll-vertical w-full h-full'
    : 'timeline-scroll w-full h-full';
  const scrollOverflowClass = hasFixedBounds
    ? 'overflow-visible'
    : isVertical
      ? 'overflow-y-auto overflow-x-auto'
      : 'overflow-x-auto overflow-y-visible';
  const scrollClass = `${baseScrollClass} ${scrollOverflowClass}`;
  const scrollStyle = isVertical
    ? {
        paddingRight: hasFixedBounds ? 40 : VERTICAL_LABEL_PADDING,
        paddingLeft: hasFixedBounds ? 40 : VERTICAL_LABEL_PADDING,
        paddingTop: 12,
        paddingBottom: 12,
      }
    : {
        paddingBottom: 12,
        paddingTop: HORIZONTAL_TOP_PADDING,
      };

  const axisWrapperStyle = isVertical
    ? { height: `${scaleFactor * effectiveZoom * 100}%`, minHeight: '100%' }
    : { width: `${scaleFactor * effectiveZoom * 100}%`, minWidth: '100%' };

  const axisLineClass = isVertical
    ? 'absolute top-1 bottom-1 left-1/2 w-0.5 bg-primary bg-opacity-40 pointer-events-none'
    : 'absolute left-1 right-1 h-0.5 bg-primary bg-opacity-40 pointer-events-none';

  const horizontalAxisPosition = `${HORIZONTAL_AXIS_OFFSET}px`;
  const axisLineStyle = isVertical
    ? undefined
    : { top: horizontalAxisPosition };

  return (
    <div className='h-full flex flex-col gap-05'>
      {showZoom && (
        <div
          className='m-1 flex flex-wrap items-center gap-1 text-2xs text-contrast text-opacity-70'
          onMouseDownCapture={stopGridDrag}
          onTouchStartCapture={stopGridDrag}
        >
          <span>Zoom</span>
          <input
            className='timeline-range flex-1'
            type='range'
            min={1}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={effectiveZoom}
            onChange={handleZoomChange}
            onMouseDown={stopGridDrag}
            onTouchStart={stopGridDrag}
            disabled={hasFixedBounds}
            style={sliderThemeVars}
          />
          <span>{effectiveZoom.toFixed(1)}×</span>
        </div>
      )}
      <div className='relative w-full flex-1'>
        <div
          ref={containerRef}
          className={scrollClass}
          style={scrollStyle}
        >
          <div
            ref={axisRef}
            className='relative h-full'
            style={axisWrapperStyle}
          >
            <div
              className={axisLineClass}
              style={axisLineStyle}
            ></div>
            {axisTicks.map(value => {
              const tickKey = formatTickKey(value);
              const hasOverride = tickLabelMap.has(tickKey);
              if (!showTicks && !hasOverride) {
                return null;
              }
              const ratio = (value - axisMin) / axisRange;
              const directedRatio = order === 'desc' ? 1 - ratio : ratio;
              const position =
                EDGE_MARGIN + directedRatio * (100 - EDGE_MARGIN * 2);
              const defaultLabel =
                Math.abs(value) >= 1000 ? Math.round(value) : value.toFixed(2);
              const overrides = tickLabelMap.get(tickKey);
              const hasBottomOverride =
                overrides?.bottom !== undefined && overrides?.bottom !== null;
              const shouldShowBottom = showTicks || hasBottomOverride;
              const bottomLabel = hasBottomOverride
                ? overrides?.bottom
                : showTicks
                  ? defaultLabel
                  : null;
              const topLabel = overrides?.top ?? null;
              if (!topLabel && !shouldShowBottom) {
                return null;
              }
              const tickStyle = isVertical
                ? {
                    top: `${position}%`,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }
                : {
                    left: `${position}%`,
                    top: horizontalAxisPosition,
                    transform: 'translate(-50%, -50%)',
                  };
              if (isVertical) {
                return (
                  <div
                    key={`tick-${value}`}
                    className='absolute pointer-events-none'
                    style={tickStyle}
                  >
                    {shouldShowBottom &&
                      bottomLabel !== null &&
                      bottomLabel !== undefined && (
                        <div
                          className='text-xs text-right text-contrast text-opacity-60 whitespace-nowrap'
                          style={{
                            position: 'absolute',
                            right: '100%',
                            marginRight: 15,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            maxWidth: 120,
                          }}
                        >
                          {bottomLabel}
                        </div>
                      )}
                    {topLabel && (
                      <div
                        className='text-xs text-left text-contrast text-opacity-60 whitespace-nowrap'
                        style={{
                          position: 'absolute',
                          left: '100%',
                          marginLeft: 15,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          maxWidth: 120,
                        }}
                      >
                        {topLabel}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div
                  key={`tick-${value}`}
                  className='absolute flex flex-col items-center text-xs text-contrast text-opacity-40 pointer-events-none'
                  style={tickStyle}
                >
                  {topLabel && (
                    <div
                      className='text-xs text-center text-contrast text-opacity-60 whitespace-nowrap mb-1'
                      style={{ maxWidth: 120 }}
                    >
                      {topLabel}
                    </div>
                  )}
                  {shouldShowBottom &&
                    bottomLabel !== null &&
                    bottomLabel !== undefined && (
                      <div
                        className={`text-xs ${
                          tickLabelsVertical
                            ? 'mt-10 origin-center rotate-90 w-5 text-center'
                            : 'mt-1 text-center whitespace-nowrap'
                        }`}
                        style={
                          tickLabelsVertical ? undefined : { maxWidth: 120 }
                        }
                      >
                        {bottomLabel}
                      </div>
                    )}
                </div>
              );
            })}
            {stackedPoints.map(
              ({ point, position, labelAlign, bucket, stackIndex }, index) => {
                const labelOffset = stackIndex * LABEL_STACK_SPACING;
                const bucketLabels = bucket.items
                  .filter(item => item.point.itemLabel)
                  .map(item => ({
                    label: item.point.itemLabel,
                    itemId: item.point.itemId,
                    color: item.point.color,
                    entryIndex: item.point.entryIndex,
                    picOptions: item.point.picOptions,
                    picKey: item.point.picSelectionKey,
                    picPreview: {
                      url: item.point.picUrl,
                      iconSlug: item.point.picIconSlug,
                    },
                  }));
                const tooltipContent = (
                  <div className='flex flex-col gap-02'>
                    <div className='text-center font-bold border-b'>
                      {point.value}
                    </div>
                    {bucketLabels.map((entry, i) => {
                      const canEditPic =
                        typeof onChangeEntryPic === 'function' &&
                        Array.isArray(entry.picOptions) &&
                        entry.picOptions.length > 0;
                      const activePicOption =
                        entry.picOptions?.find(
                          option => option.key === entry.picKey,
                        ) ||
                        entry.picOptions?.find(
                          option => option.key === 'default',
                        );
                      const activePreviewUrl =
                        activePicOption?.previewUrl || entry.picPreview?.url;
                      const activePreviewIcon =
                        activePicOption?.iconSlug || entry.picPreview?.iconSlug;
                      const picOptionsContent =
                        canEditPic && entry.picOptions ? (
                          <PicSelectionOptions
                            options={entry.picOptions}
                            value={entry.picKey}
                            size={30}
                            onChange={key =>
                              onChangeEntryPic(entry.entryIndex, key)
                            }
                          />
                        ) : null;
                      return (
                        <div
                          key={`${bucket.position}-${i}`}
                          className='flex items-center gap-1'
                        >
                          {onChangeEntryColor ? (
                            <TooltipHtml
                              placement='left'
                              title={
                                <ColorsSwatch
                                  currentColor={entry.color}
                                  colors={COLOR_KEYS}
                                  onClick={color =>
                                    onChangeEntryColor(entry.entryIndex, color)
                                  }
                                />
                              }
                            >
                              <div
                                className='w-1 h-1 rounded-full cursor-pointer'
                                style={{
                                  backgroundColor: resolveColor(entry.color),
                                }}
                                onClick={e => e.stopPropagation()}
                              ></div>
                            </TooltipHtml>
                          ) : (
                            <div
                              className='w-1 h-1 rounded-full'
                              style={{
                                backgroundColor: resolveColor(entry.color),
                              }}
                            ></div>
                          )}
                          {canEditPic && picOptionsContent && (
                            <TooltipHtml
                              placement='left'
                              title={picOptionsContent}
                            >
                              <div
                                className='rounded border border-primary border-opacity-20 bg-main bg-opacity-40 flex items-center justify-center overflow-hidden cursor-pointer'
                                style={{ width: 30, height: 30 }}
                                onClick={e => e.stopPropagation()}
                              >
                                {renderPicPreview(
                                  activePreviewUrl,
                                  activePreviewIcon,
                                  30,
                                )}
                              </div>
                            </TooltipHtml>
                          )}
                          <span
                            className='cursor-pointer hover:text-secondary flex-1 text-left'
                            onClick={e => {
                              e.stopPropagation();
                              dispatch(setDrawerOpen(entry.itemId));
                            }}
                          >
                            {entry.label}
                          </span>
                          {typeof onRemoveEntry === 'function' && (
                            <div
                              className='text-xs text-contrast text-opacity-60 cursor-pointer hover:text-secondary transition'
                              onDoubleClick={e => {
                                e.stopPropagation();
                                onRemoveEntry(entry.entryIndex);
                              }}
                              title='Double-cliquer pour retirer de la timeline'
                            >
                              <FaTimes />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );

                const pointStyle = isVertical
                  ? {
                      top: `${position}%`,
                      left: '50%',
                    }
                  : {
                      left: `${position}%`,
                      top: horizontalAxisPosition,
                    };

                const labelStyle = isVertical
                  ? {
                      marginBottom: 4,
                      position: 'relative',
                      right: labelOffset,
                      textAlign: 'right',
                      maxWidth: 120,
                    }
                  : {
                      textAlign: labelAlign,
                      marginBottom: 4,
                      position: 'relative',
                      top: -labelOffset,
                      maxWidth: 120,
                    };

                const sizeKey = point.size || 'medium';
                const sizeConfig = {
                  small: { diameter: 16, border: 2 },
                  medium: { diameter: 24, border: 2 },
                  large: { diameter: 32, border: 3 },
                };
                const config = sizeConfig[sizeKey] || sizeConfig.medium;
                const diameter = config.diameter;
                const borderWidth = config.border;
                const iconElement =
                  point.picIconSlug && diameter - borderWidth * 2 > 0
                    ? renderIconBySlug(point.picIconSlug, {
                        size: diameter - borderWidth * 2,
                        color: 'var(--contrast)',
                      })
                    : null;
                const hasIcon = Boolean(iconElement);
                const circleNode = (
                  <div
                    className={`rounded-full shadow overflow-hidden flex items-center justify-center transition-all ${
                      pointsAreDraggable
                        ? 'cursor-grab active:cursor-grabbing'
                        : 'cursor-pointer'
                    }`}
                    style={{
                      width: diameter,
                      height: diameter,
                      background:
                        !hasIcon && point.picUrl
                          ? `url(${point.picUrl}) center center / cover`
                          : undefined,
                      backgroundColor:
                        hasIcon || !point.picUrl
                          ? resolveColor(point.color)
                          : undefined,
                      border: `${borderWidth}px solid ${resolveColor(
                        point.color,
                      )}`,
                    }}
                    onClick={() => handlePointClick(point)}
                    onPointerDown={event =>
                      handlePointPointerDown(event, point.entryIndex)
                    }
                  >
                    {hasIcon && (
                      <span className='text-contrast text-opacity-90'>
                        {iconElement}
                      </span>
                    )}
                    {!hasIcon && point.picUrl && (
                      <span className='sr-only'>{point.itemLabel}</span>
                    )}
                  </div>
                );

                const pointContent = (
                  <div className='flex flex-col items-center gap-0 -translate-x-1/2 -translate-y-1/2'>
                    {showLabels &&
                      (showPointTooltips ? (
                        <TooltipHtml title={tooltipContent}>
                          <div
                            className='cursor-pointer'
                            onClick={() => handlePointClick(point)}
                          >
                            <div
                              className='text-xs text-contrast text-opacity-70 whitespace-nowrap overflow-hidden text-ellipsis'
                              style={labelStyle}
                            >
                              {point.itemLabel}
                            </div>
                          </div>
                        </TooltipHtml>
                      ) : (
                        <div
                          className='cursor-pointer'
                          onClick={() => handlePointClick(point)}
                        >
                          <div
                            className='text-xs text-contrast text-opacity-70 whitespace-nowrap overflow-hidden text-ellipsis'
                            style={labelStyle}
                          >
                            {point.itemLabel}
                          </div>
                        </div>
                      ))}
                    {showPointTooltips ? (
                      <TooltipHtml title={tooltipContent}>
                        {circleNode}
                      </TooltipHtml>
                    ) : (
                      circleNode
                    )}
                    {showValues && (
                      <div className='text-3xs text-contrast text-opacity-50 whitespace-nowrap'>
                        {point.value}
                      </div>
                    )}
                  </div>
                );

                if (disableLayoutAnimation) {
                  return (
                    <div
                      key={point.id ?? index}
                      className='absolute'
                      style={pointStyle}
                    >
                      {pointContent}
                    </div>
                  );
                }

                return (
                  <motion.div
                    layout
                    transition={{
                      type: 'spring',
                      stiffness: 260,
                      damping: 30,
                    }}
                    key={point.id ?? index}
                    className='absolute'
                    style={pointStyle}
                  >
                    {pointContent}
                  </motion.div>
                );
              },
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
